/**
 * Der globale Look eines Scrollys — EINE Auflösung für Editor und Leser.
 *
 * DAS PROBLEM (Jans Fund 05.08.2026: „Der Template-Scrolly übernimmt nicht die
 * Pro-Einstellungen aus GLOBAL, zum Beispiel Sättigung"): Helligkeit, Kontrast,
 * Sättigung, Temperatur, Vignette und Korn wurden ausschliesslich in der
 * Pro-Editor-Hülle gezeichnet — als CSS über der Vorschau. Der geteilte Player,
 * den Template-Editor-Vorschau UND veröffentlichter Scrolly benutzen, kannte
 * die Werte nicht einmal. Sie waren also gespeichert und nirgends zu sehen.
 *
 * WAS EINGEFÄRBT WIRD: die BÜHNE (3D/Bild und die Ebenen darauf) — nicht die
 * HTML-Textboxen. Jans Entscheidung 05.08.2026: „Ein Bild-Look soll das Bild
 * treffen, und lesbarer Text auf gefärbtem Grund ist genau der Punkt, an dem
 * die Box arbeitet."
 *
 * WERTEBEREICHE (wie im GLOBAL-Reiter):
 *   Helligkeit / Kontrast / Sättigung  1 = neutral
 *   Temperatur                        -1 kühl … 0 neutral … 1 warm
 *   Vignette / Korn                    0 = aus … 1 = stark
 */

export interface GlobalLookOptions {
    lookBrightness?: number | null;
    lookContrast?: number | null;
    lookSaturation?: number | null;
    lookTemperature?: number | null;
    lookVignette?: number | null;
    lookGrain?: number | null;
}

export interface GlobalLook {
    /** CSS-`filter` für die Bühne; `undefined`, wenn alles neutral steht. */
    filter?: string;
    /** −1 … 1 */
    temperature: number;
    /** 0 … 1 */
    vignette: number;
    /** 0 … 1 */
    grain: number;
    /** Braucht es die Overlay-Ebene (Temperatur/Vignette/Korn) überhaupt? */
    hatOverlay: boolean;
}

function zahl(wert: unknown, vorgabe: number): number {
    return typeof wert === "number" && Number.isFinite(wert) ? wert : vorgabe;
}

export function resolveGlobalLook(
    options: GlobalLookOptions | null | undefined,
): GlobalLook {
    const brightness = zahl(options?.lookBrightness, 1);
    const contrast = zahl(options?.lookContrast, 1);
    const saturation = zahl(options?.lookSaturation, 1);
    const temperature = zahl(options?.lookTemperature, 0);
    const vignette = zahl(options?.lookVignette, 0);
    const grain = zahl(options?.lookGrain, 0);

    // Nichts eingestellt → gar kein Filter. Ein `filter` erzeugt einen eigenen
    // Container-Block und einen Kompositor-Layer; ohne Wirkung wäre das nur
    // Kosten (und im Zweifel eine Nebenwirkung auf `position: fixed`-Kinder).
    const neutral = brightness === 1 && contrast === 1 && saturation === 1;

    return {
        filter: neutral
            ? undefined
            : `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`,
        temperature,
        vignette,
        grain,
        hatOverlay: temperature !== 0 || vignette > 0 || grain > 0,
    };
}
